import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { DiscardConfirmHost } from "@/components/DiscardConfirmHost";
import { router } from "@/router";
import { usePendingWritesTracker } from "@/lib/pending-writes-tracker";
import { GlobalErrorToast } from "@/components/GlobalErrorToast";
import { RulesBulkJobsToaster } from "@/components/RulesBulkJobsToaster";
import { setGlobalError } from "@/lib/global-error-toast";

// One shared QueryClient per app instance. `staleTime` is conservative —
// Overview and Ads Manager override per-query when they want tighter
// freshness (e.g. polling every 5 minutes for live spend).
//
// QueryCache + MutationCache `onError` push a global toast on failure.
// Without this, a 500 from any endpoint surfaced as a silent empty
// table — the bug class that hid `column ag.app_id does not exist`
// from us until manual prod probing on 2026-05-05.
function describeError(error: unknown, fallback: string): string | null {
  const status =
    (error as { response?: { status?: number } })?.response?.status ?? 0;
  // Auth-related — handled (or about-to-be-handled) by the axios
  // interceptor / route gates. A toast on top is just noise.
  if (status === 401 || status === 403 || status === 423) return null;
  const detail = (
    error as {
      response?: { data?: { error?: { message?: string }; message?: string } };
      message?: string;
    }
  )?.response?.data?.error?.message
    ?? (
      error as {
        response?: { data?: { message?: string } };
      }
    )?.response?.data?.message
    ?? (error as { message?: string })?.message
    ?? "Network error";
  return status >= 500
    ? `${fallback}. Server returned ${status}.`
    : `${fallback}: ${detail}`;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      const msg = describeError(error, "Couldn't load data");
      if (msg) setGlobalError(msg);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      const msg = describeError(error, "Action failed");
      if (msg) setGlobalError(msg);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry auth failures — the axios interceptor already
        // tried to refresh once; further retries just spin.
        const status =
          (error as { response?: { status?: number } })?.response?.status ?? 0;
        if (status === 401 || status === 403 || status === 423) return false;
        return failureCount < 2;
      },
    },
  },
});

// Mounts the global pending-writes poller alongside the router. Lives
// here (not in some shell layout) so any mutation, anywhere in the
// app, gets its 1.5-second confirm-and-invalidate loop — including
// pages that don't render the Ads Manager toolbar.
function RoutedShell() {
  usePendingWritesTracker();
  return <RouterProvider router={router} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RoutedShell />
        <GlobalErrorToast />
        <RulesBulkJobsToaster />
        <DiscardConfirmHost />
      </AuthProvider>
    </QueryClientProvider>
  );
}
